import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Loader2 } from "lucide-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useOnboarding } from "@/hooks/useOnboarding";
import OnboardingPhotoUpload from "@/components/onboarding/OnboardingPhotoUpload";
import OnboardingVideoUpload from "@/components/onboarding/OnboardingVideoUpload";
import OnboardingProfileInfo from "@/components/onboarding/OnboardingProfileInfo";
import OnboardingPreview from "@/components/onboarding/OnboardingPreview";

type Step = "form" | "preview";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, refreshProfile, signOut } = useAuthContext();
  const [step, setStep] = useState<Step>("form");
  const [editSection, setEditSection] = useState<"photos" | "video" | "info" | null>(null);

  const {
    photos,
    addPhoto,
    removePhoto,
    replacePhoto,
    video,
    setVideoFile,
    removeVideo,
    data,
    updateData,
    updateUsername,
    checkUsernameAvailability,
    usernameError,
    checkingUsername,
    countries,
    states,
    cities,
    fetchCountries,
    selectCountry,
    selectState,
    selectCity,
    loadingCountries,
    loadingStates,
    loadingCities,
    requiresState,
    getCountryFlag,
    isFemale,
    canSubmit,
    getValidationErrors,
    submitting,
    submitOnboarding,
    cleanup,
  } = useOnboarding();

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  const handleContinue = () => {
    if (canSubmit()) {
      setStep("preview");
    }
  };

  const handleBack = () => {
    if (step === "preview") {
      if (editSection) {
        setEditSection(null);
      } else {
        setStep("form");
      }
    }
  };

  const handleSubmit = async () => {
    if (!user?.id) return;
    
    const success = await submitOnboarding(user.id);
    if (success) {
      // Refresh the profile in AuthContext before navigating
      // This ensures OnboardingGate sees the updated photo_url
      await refreshProfile();
      navigate("/profile");
    }
  };

  const handleEditPhotos = () => {
    setEditSection("photos");
  };

  const handleEditVideo = () => {
    setEditSection("video");
  };

  const handleEditInfo = () => {
    setEditSection("info");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-pop border-t-transparent animate-spin" />
      </div>
    );
  }

  const validationErrors = getValidationErrors();

  return (
    <div className="min-h-screen sky-gradient">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={async () => {
              if (step === "preview") {
                handleBack();
              } else {
                try {
                  const { error } = await signOut();
                  if (!error) {
                    navigate("/entry", { replace: true });
                  }
                } catch (err) {
                  console.error("Sign out error:", err);
                }
              }
            }} 
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-pop flex items-center justify-center">
              <Heart className="w-4 h-4 text-white fill-white" />
            </div>
            <span className="font-semibold">Pop Balloon</span>
          </div>

          <div className="w-9" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 pb-32">
        {step === "form" && (
          <div className="space-y-8">
            {/* Title */}
            <div className="text-center">
              <h1 className="text-2xl font-bold">Create Your Profile</h1>
              <p className="text-muted-foreground mt-1">
                Let's set up your profile to start meeting people
              </p>
            </div>

            {/* Profile Info */}
            <OnboardingProfileInfo
              data={data}
              onUpdateData={updateData}
              onUpdateUsername={updateUsername}
              onCheckUsernameAvailability={checkUsernameAvailability}
              usernameError={usernameError}
              checkingUsername={checkingUsername}
              countries={countries}
              states={states}
              cities={cities}
              onSelectCountry={selectCountry}
              onSelectState={selectState}
              onSelectCity={selectCity}
              loadingCountries={loadingCountries}
              loadingStates={loadingStates}
              loadingCities={loadingCities}
              onFetchCountries={fetchCountries}
              requiresState={requiresState}
              getCountryFlag={getCountryFlag}
            />

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Photos */}
            <OnboardingPhotoUpload
              photos={photos}
              onAddPhoto={addPhoto}
              onRemovePhoto={removePhoto}
              onReplacePhoto={replacePhoto}
            />

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Video */}
            <OnboardingVideoUpload
              video={video}
              onSetVideo={setVideoFile}
              onRemoveVideo={removeVideo}
              isRequired={isFemale}
            />
          </div>
        )}

        {step === "preview" && !editSection && (
          <OnboardingPreview
            photos={photos}
            video={video}
            data={data}
            onEditPhotos={handleEditPhotos}
            onEditVideo={handleEditVideo}
            onEditInfo={handleEditInfo}
          />
        )}

        {step === "preview" && editSection === "photos" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Edit Photos</h2>
            <OnboardingPhotoUpload
              photos={photos}
              onAddPhoto={addPhoto}
              onRemovePhoto={removePhoto}
              onReplacePhoto={replacePhoto}
            />
            <button
              onClick={() => setEditSection(null)}
              className="w-full py-3 rounded-xl bg-pop text-pop-foreground font-medium"
            >
              Done
            </button>
          </div>
        )}

        {step === "preview" && editSection === "video" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Edit Video</h2>
            <OnboardingVideoUpload
              video={video}
              onSetVideo={setVideoFile}
              onRemoveVideo={removeVideo}
              isRequired={isFemale}
            />
            <button
              onClick={() => setEditSection(null)}
              className="w-full py-3 rounded-xl bg-pop text-pop-foreground font-medium"
            >
              Done
            </button>
          </div>
        )}

        {step === "preview" && editSection === "info" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold">Edit Info</h2>
            <OnboardingProfileInfo
              data={data}
              onUpdateData={updateData}
              onUpdateUsername={updateUsername}
              onCheckUsernameAvailability={checkUsernameAvailability}
              usernameError={usernameError}
              checkingUsername={checkingUsername}
              countries={countries}
              states={states}
              cities={cities}
              onSelectCountry={selectCountry}
              onSelectState={selectState}
              onSelectCity={selectCity}
              loadingCountries={loadingCountries}
              loadingStates={loadingStates}
              loadingCities={loadingCities}
              onFetchCountries={fetchCountries}
              requiresState={requiresState}
              getCountryFlag={getCountryFlag}
            />
            <button
              onClick={() => setEditSection(null)}
              className="w-full py-3 rounded-xl bg-pop text-pop-foreground font-medium"
            >
              Done
            </button>
          </div>
        )}
      </main>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-lg border-t border-border p-4 safe-area-pb">
        <div className="max-w-lg mx-auto">
          {step === "form" && (
            <button
              onClick={handleContinue}
              disabled={!canSubmit()}
              className="w-full py-4 rounded-xl pop-gradient text-white font-semibold shadow-button hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:hover:scale-100"
            >
              Preview Profile
            </button>
          )}

          {step === "preview" && !editSection && (
            <button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit()}
              className="w-full py-4 rounded-xl pop-gradient text-white font-semibold shadow-button hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
              {submitting ? "Creating Profile..." : "Create Profile"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
